import React, { useContext, useMemo } from 'react';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';

import ModuleLabelIcon from 'pages/ZonesV2/Icons/LabelFilterSlideoutIcon';
import LabelList from './LabelList/LabelList';

import LabelPreview from './LabelPreview/LabelPreview';
import LabelConfiguration from './LabelConfiguration';

import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import './LabelFilterSlideout.scss';
import PrioritizationContext from 'pages/ZonesV2/PrioritizationContext/PrioritizationContext';
import LabelFilterContext from './LabelFilterContext/LabelFilterContext';
import { LabelFilter } from 'pages/Zones/types';
import { getIcon } from '@flybits/design-system';
// Ensure the path and export are correct; adjust if needed
import { validateLabelManagerContentLimit } from 'components/Zones/v2/zone.validators';

type TLabelFilterSlideoutProps = {
  onClickClose: () => void;
  onClickApply: (labelFilters: LabelFilter[]) => void;
  onSlideOutComplete: () => void;
};

const MAIN_CLASS = 'module-label-filters-slide-out';
const CLASSES = {
  GRID: `${MAIN_CLASS}__grid-container`,
  PANEL: `${MAIN_CLASS}__panel`,
  LABEL_LIST: `${MAIN_CLASS}__label-list-container`,
  LABEL_INFO: `${MAIN_CLASS}__label-info-container`,
  LABEL_PREVIEW: `${MAIN_CLASS}__label-preview-container`,
  TOOL_TIP: `${MAIN_CLASS}__tool-tip`,
};

const LabelFilterSlideout: React.FC<TLabelFilterSlideoutProps> = ({
  onClickClose,
  onClickApply,
  onSlideOutComplete,
}) => {
  const { labelFilters, pageSize, selectedLabelFilter } = useContext(LabelFilterContext);
  const { isLabelManagementOpen } = useContext(PrioritizationContext);

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      headerInfo: {
        mainTitle: '',
        goBackTitle: 'Label Manager',
        goBackSubTitle: 'Manage and prioritize your labels',
        goBackIcon: <ModuleLabelIcon />,
        goBackActionHandler: onClickClose,
      },
      footerInfo: {
        primaryActionText: 'Apply',
        primaryActionHandler: () => {
          onClickApply(labelFilters ?? []);
        },
        secondaryActionText: 'Cancel',
        secondaryActionHandler: onClickClose,
        primaryActionDisabled: validateLabelManagerContentLimit(selectedLabelFilter?.limit) !== '',
      },
      showFooter: true,
      show: isLabelManagementOpen,
      size: 'large',
      className: MAIN_CLASS,
      onSlideOutComplete,
    }),
    [labelFilters, isLabelManagementOpen, onClickApply, onClickClose, onSlideOutComplete, selectedLabelFilter],
  );

  return (
    <SlidingSidePanel {...slidingSidePanelProps}>
      <div className={CLASSES.TOOL_TIP}>
        {getIcon('infoFilled', {})}
        <div className={`${CLASSES.TOOL_TIP}__text`}>
          {`End users may not see all content due to a content limit of ${pageSize} on this module.`}
        </div>
      </div>
      <div className={CLASSES.GRID}>
        <div className={`${CLASSES.LABEL_LIST} ${CLASSES.PANEL}`}>
          <LabelList />
        </div>
        <div className={`${CLASSES.LABEL_INFO} ${CLASSES.PANEL}`}>
          <div className={CLASSES.LABEL_INFO}>
            <LabelConfiguration />
          </div>
        </div>
        <div className={`${CLASSES.LABEL_PREVIEW} ${CLASSES.PANEL}`}>
          <LabelPreview />
        </div>
      </div>
    </SlidingSidePanel>
  );
};

export default LabelFilterSlideout;
